import React, { useState } from 'react';
import { RoleDTO } from '@/models';
import { PermissionGroup } from '@/hooks/useUserPermissionManagement';
import { Alert } from '@/components/common';

interface PermissionMatrixProps {
  roles: RoleDTO[];
  allPermissionRows: PermissionGroup[];
  selectedPermissionIds: Set<string>;
  onToggle: (permissionId: string) => void;
  onSelectAllForRole: (roleId: string, selectAll: boolean) => void;
  onReplaceWithRole: (roleId: string) => void;
  onSetFullAccess: (enabled: boolean) => void;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  roles,
  allPermissionRows,
  selectedPermissionIds,
  onToggle,
  onSelectAllForRole,
  onReplaceWithRole,
  onSetFullAccess,
}) => {
  const [presetRoleId, setPresetRoleId] = useState('');

  const fullAccessGroup = allPermissionRows.find((g) => g.prefix === 'FullAccess');
  const tableGroups = allPermissionRows.filter((g) => g.prefix !== 'FullAccess');

  const isFullAccessEnabled =
    (fullAccessGroup?.permissions.some((p) => selectedPermissionIds.has(p.id)) ?? false);

  if (allPermissionRows.length === 0) {
    return (
      <div className="row">
        <div className="col-12">
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            No permissions available.
          </Alert>
        </div>
      </div>
    );
  }

  const totalCount = tableGroups.reduce((sum, g) => sum + g.permissions.length, 0);
  const selectedCount = tableGroups.reduce(
    (sum, g) => sum + g.permissions.filter((p) => selectedPermissionIds.has(p.id)).length,
    0
  );
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && !allSelected;

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setPresetRoleId(id);
    if (id) onReplaceWithRole(id);
  };

  const handleSelectAll = () => {
    for (const role of roles) {
      onSelectAllForRole(role.id, !allSelected);
    }
  };

  return (
    <div className="row">
      <div className="col-6">
        <div className="card">
          <div className="card-body">

            {/* Full Access toggle */}
            {fullAccessGroup && (
              <div
                className={`d-flex align-items-center justify-content-between p-3 rounded mb-3 ${
                  isFullAccessEnabled ? 'bg-danger bg-opacity-10 border border-danger border-opacity-25' : 'bg-light border'
                }`}
              >
                <div>
                  <div className="fw-semibold">
                    <i className="fas fa-crown me-2 text-danger"></i>
                    Full Access
                  </div>
                  <div className="text-muted small mt-1">
                    Grants unrestricted access. All other permissions are ignored when enabled.
                  </div>
                </div>
                <div className="form-check form-switch ms-3 mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="full-access-toggle"
                    style={{ width: '2.5em', height: '1.3em', cursor: 'pointer' }}
                    checked={isFullAccessEnabled}
                    onChange={(e) => onSetFullAccess(e.target.checked)}
                  />
                  <label
                    className="form-check-label fw-semibold ms-1"
                    htmlFor="full-access-toggle"
                    style={{ cursor: 'pointer' }}
                  >
                    {isFullAccessEnabled ? (
                      <span className="text-danger">Enabled</span>
                    ) : (
                      <span className="text-muted">Disabled</span>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Role preset */}
            <div className="row mb-3">
              <div className="col-md-5 col-lg-4">
                <label className="form-label fw-semibold small text-muted">
                  <i className="fas fa-user-shield me-2"></i>Apply role preset
                </label>
                <select
                  className="form-select form-select-sm"
                  value={presetRoleId}
                  onChange={handlePresetChange}
                  disabled={roles.length === 0 || isFullAccessEnabled}
                >
                  <option value="">Select a role to apply…</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name ?? 'Unknown Role'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Summary bar */}
            <div
              className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
              style={{ opacity: isFullAccessEnabled ? 0.4 : 1 }}
            >
              <span className="text-muted small">
                <strong>{selectedCount}</strong> of <strong>{totalCount}</strong> permissions
                enabled
              </span>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="select-all-global"
                  className="form-check-input"
                  style={{ cursor: 'pointer' }}
                  checked={allSelected}
                  disabled={isFullAccessEnabled}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  onChange={handleSelectAll}
                />
                <label
                  htmlFor="select-all-global"
                  className="form-check-label small text-muted mb-0"
                  style={{ cursor: isFullAccessEnabled ? 'default' : 'pointer' }}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </label>
              </div>
            </div>

            {/* Permission table */}
            <div
              className="table-responsive"
              style={{ opacity: isFullAccessEnabled ? 0.4 : 1, pointerEvents: isFullAccessEnabled ? 'none' : 'auto' }}
            >
              <table className="table table-bordered table-hover table-sm align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Permission</th>
                    <th className="text-center" style={{ width: 90 }}>
                      Enabled
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableGroups.map((group) => (
                    <React.Fragment key={group.prefix}>
                      <tr className="table-secondary">
                        <td
                          colSpan={2}
                          className="fw-bold py-1 px-3 small text-uppercase text-muted"
                        >
                          <i className="fas fa-layer-group me-2"></i>
                          {group.prefix}
                        </td>
                      </tr>
                      {group.permissions.map((permission) => (
                        <tr key={permission.id}>
                          <td>
                            <label
                              htmlFor={`perm-${permission.id}`}
                              className="mb-0 w-100"
                              style={{ cursor: 'pointer' }}
                              title={permission.key ?? undefined}
                            >
                              {permission.name}
                            </label>
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              id={`perm-${permission.id}`}
                              className="form-check-input"
                              style={{ cursor: 'pointer' }}
                              checked={selectedPermissionIds.has(permission.id)}
                              onChange={() => onToggle(permission.id)}
                              aria-label={permission.name}
                            />
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
